import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppConst from "../apis/appconst";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import Select from "react-select";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

/**
 * EditorPanel - reusable CKEditor panel
 */
function EditorPanel({ id, label, value, onChange, open, onToggle }) {
  const localRef = useRef(null);
  const ckKey = `${id}-${label}`;

  useEffect(() => {
    try {
      const ed = localRef.current;
      if (ed && typeof ed.setData === 'function') {
        const cur = (typeof ed.getData === 'function') ? ed.getData() : '';
        if ((cur || '').trim() !== (value || '').trim()) {
          ed.setData(value || '');
        }
      }
    } catch (e) {
      // ignore sync errors
    }
  }, [value]);

  return (
    <div className="col-md-6 mb-3">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <label className="form-label mb-0">{label}</label>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary toggle-btn"
          aria-expanded={!!open}
          onClick={() => onToggle && onToggle()}
          title={open ? `Collapse ${label}` : `Expand ${label}`}
        >
          <span style={{ fontSize: 16 }}>{open ? '−' : '+'}</span>
        </button>
      </div>

      <div id={`editor-${id}-${label}`}>
        {!open ? (
          <div
            className="collapsed-preview card p-2"
            style={{ minHeight: 70, cursor: 'pointer' }}
            onClick={() => onToggle && onToggle()}
            dangerouslySetInnerHTML={{
              __html: value || `<span class="text-muted">No ${label.toLowerCase()} entered</span>`
            }}
          />
        ) : (
          <CKEditor
            key={ckKey}
            editor={ClassicEditor}
            data={value || ''}
            onReady={(editor) => {
              localRef.current = editor;
            }}
            onChange={(event, editor) => {
              try {
                const data = editor.getData();
                onChange && onChange(data);
              } catch (err) {
                console.warn(err);
              }
            }}
            onBlur={(event, editor) => {
              try {
                const data = editor.getData();
                onChange && onChange(data);
              } catch (_) {}
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * MedicineModal - Modal for adding/editing medicines with QUANTITY inputs
 */
function MedicineModal({ show, onClose, medicineOptions, selectedMedicines, onSave, loading }) {
  const [localMedicines, setLocalMedicines] = useState([]);

  useEffect(() => {
    if (show) {
      setLocalMedicines([...selectedMedicines]);
    }
  }, [show, selectedMedicines]);

  const handleMedicineSelect = (selected) => {
    const withDosage = selected.map((s) => {
      const existing = localMedicines.find((m) => m.value === s.value);
      return existing ? existing : {
        ...s,
        morning: '',
        noon: '',
        evening: '',
        night: '',
      };
    });
    setLocalMedicines(withDosage);
  };

  const handleDosageChange = (medId, dosageType, value) => {
    // Only allow numbers
    const numValue = value.replace(/[^0-9]/g, '');
    const updated = localMedicines.map((m) =>
      m.value === medId ? { ...m, [dosageType]: numValue } : m
    );
    setLocalMedicines(updated);
  };

  const handleRemoveMedicine = (medId) => {
    const updated = localMedicines.filter((m) => m.value !== medId);
    setLocalMedicines(updated);
  };

  const handleSaveClick = () => {
    onSave(localMedicines);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="ri-medicine-bottle-line me-2"></i>
              Manage Medicines
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-bold">Search and Select Medicines</label>
              <Select
                isMulti
                options={medicineOptions}
                isLoading={loading}
                onChange={handleMedicineSelect}
                value={localMedicines}
                placeholder="Type to search medicines..."
                className="react-select-container"
                classNamePrefix="react-select"
              />
              <small className="text-muted">Select medicines and enter dosage quantities (1, 2, 3, etc.) for each time</small>
            </div>

            {localMedicines.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-hover">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '35%' }}>Medicine Name</th>
                      <th className="text-center" style={{ width: '15%' }}>
                        <i className="ri-sun-line"></i> Morning
                      </th>
                      <th className="text-center" style={{ width: '15%' }}>
                        <i className="ri-sun-fill"></i> Noon
                      </th>
                      <th className="text-center" style={{ width: '15%' }}>
                        <i className="ri-moon-line"></i> Evening
                      </th>
                      <th className="text-center" style={{ width: '15%' }}>
                        <i className="ri-moon-fill"></i> Night
                      </th>
                      <th className="text-center" style={{ width: '7%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localMedicines.map((med, index) => (
                      <tr key={med.value}>
                        <td className="text-center align-middle">{index + 1}</td>
                        <td className="align-middle"><strong>{med.label}</strong></td>
                        <td className="text-center">
                          <input
                            type="text"
                            className="form-control form-control-sm text-center"
                            value={med.morning || ''}
                            onChange={(e) => handleDosageChange(med.value, 'morning', e.target.value)}
                            placeholder="0"
                            maxLength="2"
                            style={{ width: '60px', margin: '0 auto' }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="text"
                            className="form-control form-control-sm text-center"
                            value={med.noon || ''}
                            onChange={(e) => handleDosageChange(med.value, 'noon', e.target.value)}
                            placeholder="0"
                            maxLength="2"
                            style={{ width: '60px', margin: '0 auto' }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="text"
                            className="form-control form-control-sm text-center"
                            value={med.evening || ''}
                            onChange={(e) => handleDosageChange(med.value, 'evening', e.target.value)}
                            placeholder="0"
                            maxLength="2"
                            style={{ width: '60px', margin: '0 auto' }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="text"
                            className="form-control form-control-sm text-center"
                            value={med.night || ''}
                            onChange={(e) => handleDosageChange(med.value, 'night', e.target.value)}
                            placeholder="0"
                            maxLength="2"
                            style={{ width: '60px', margin: '0 auto' }}
                          />
                        </td>
                        <td className="text-center align-middle">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveMedicine(med.value)}
                            title="Remove medicine"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="ri-information-line me-2"></i>
                No medicines selected. Please search and select medicines from the dropdown above.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>
              <i className="ri-close-line me-1"></i>Cancel
            </button>
            <button type="button" className="btn btn-success" onClick={handleSaveClick}>
              <i className="ri-save-line me-1"></i>Save Medicines
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main DischargeSummary Component
 */
function DischargeSummary({ patientData: propPatientData = null, onClose, onSaved, modalMode = false }) {
  const [formData, setFormData] = useState({ doc: [] });
  const [isSaved, setIsSaved] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState(null);
  const [watermarkImage, setWatermarkImage] = useState('');
  const [footerImage, setFooterImage] = useState('');
  const [image, setImage] = useState('');
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [caseTitle, setCaseTitle] = useState('');

  // Medicine states
  const [caseMedicines, setCaseMedicines] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [showMedicineModal, setShowMedicineModal] = useState(false);
  const [medicineLoading, setMedicineLoading] = useState(false);
  const billResolvedRef = useRef(false);
   // ✅ Prefer backend's formatted IP/yy/NN; fall back to a built format
   const ipdDisplay = (() => {
    if (patientData?.ipd_display_no) return patientData.ipd_display_no;   // e.g. "IP/26/090"

    const raw = patientData?.ipd_no || patientData?.ipd_id || patientData?.ipd_bill_id || '';
    if (!raw) return '';

    // build IP/yy/NN from admission/billing date
    const srcDt = patientData?.ipd_datetime || patientData?.ipd_bill_doa || patientData?.ipd_bill_date || '';
    let yy = new Date().getFullYear().toString().slice(-2);
    if (srcDt) {
      const ts = new Date(String(srcDt).replace(' ', 'T'));
      if (!isNaN(ts.getTime())) yy = ts.getFullYear().toString().slice(-2);
    }
    return `IP/${yy}/${String(raw).padStart(2, '0')}`;
  })();

  // Editor open state
  const [editorOpen, setEditorOpen] = useState({
    diagnosis: false,
    procedure: false,
    courseInHospital: false,
    clinicalHistory: false,
    physicalExam: false,
    generalExam: false,
    systematicExam: false,
    advice: false,
    notes: false
  });

  const toggleEditor = (key) => {
    setEditorOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const showSwal = (options) => {
    const baseOptions = modalMode ? {
      ...options,
      customClass: {
        container: 'swal-above-modal',
        popup: 'swal-popup-above-modal',
        ...(options.customClass || {})
      },
      didOpen: (popup) => {
        const container = popup.parentElement;
        if (container) {
          container.style.zIndex = '200000';
        }
        if (options.didOpen) options.didOpen(popup);
      }
    } : options;
    
    return Swal.fire(baseOptions);
  };

  const getNowDateTimeLocal = () => {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const safeIsoDate = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (!s || s === "0000-00-00" || s === "0000-00-00 00:00:00") return "";
    const norm = s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
    const d = new Date(norm);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const [dischargeInfo, setDischargeInfo] = useState({
    dateOfSurgery: '',
    dateOfDischarge: getNowDateTimeLocal(),
    result: '',
    notes: '',
    diagnosis: '',
    procedure: '',
    courseInHospital: '',
    clinicalHistory: '',
    physicalExam: '',
    generalExam: '',
    systematicExam: '',
    advice: ''
  });

  // Helper function to format dosage display
  const formatDosageTiming = (med) => {
    const parts = [];
    if (med.morning && med.morning !== '0') parts.push(`M: ${med.morning}`);
    if (med.noon && med.noon !== '0') parts.push(`N: ${med.noon}`);
    if (med.evening && med.evening !== '0') parts.push(`E: ${med.evening}`);
    if (med.night && med.night !== '0') parts.push(`Nt: ${med.night}`);
    return parts.length > 0 ? parts.join(', ') : 'As directed';
  };

  // Fetch medicine list from backend
  const fetchMedicineList = async () => {
    setMedicineLoading(true);
    try {
      const fd = new FormData();
      const response = await axios.post(
        AppConst.BASE_URL + AppConst.getMedListUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.status && response.data.records) {
        const meds = response.data.records.map((m) => ({
          value: m.med_id,
          label: m.med_name,
        }));
        setMedicineOptions(meds);
      }
    } catch (err) {
      console.error("Error fetching medicines:", err);
      toast.error("Failed to load medicine list");
    } finally {
      setMedicineLoading(false);
    }
  };

  // Handle medicine modal save
  const handleMedicineSave = (medicines) => {
    setCaseMedicines(medicines);
    toast.success(`${medicines.length} medicine(s) saved`);
  };

  const normalizePatient = (pd) => {
    if (!pd) return pd;
    const uhid = pd.ipd_bill_uhid ?? pd.ipd_uhid ?? pd.uhid ?? pd.ot_uhid_no ?? pd.ipd_bill_patient_uhid ?? '';
    const caseId = pd.ipd_bill_case ?? pd.case_id ?? pd.ipd_case ?? '';
    return {
      ...pd,
      ipd_bill_uhid: pd.ipd_bill_uhid ?? uhid,
      ipd_uhid: pd.ipd_uhid ?? uhid,
      uhid: pd.uhid ?? uhid,
      ot_uhid_no: pd.ot_uhid_no ?? uhid,
      ipd_bill_case: caseId,
    };
  };

  const loadCaseDetails = async (cat_id) => {
    if (!cat_id) return null;
    try {
      const fd = new FormData();
      fd.append("cat_id", cat_id);
      const response = await axios.post(AppConst.BASE_URL + AppConst.getCatByIdUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      if (response.data && response.data.status && response.data.cat_dt) {
        const cd = response.data.cat_dt;
  
        const mapped = {
          diagnosis: cd.diagnosis || cd.ipd_diagnosis || cd.case_diagnosis || dischargeInfo.diagnosis || '',
          procedure: cd.procedure || cd.ipd_procedure || cd.procedures || dischargeInfo.procedure || '',
          courseInHospital: cd.course_in_hospital || cd.course || cd.courseInHospital || dischargeInfo.courseInHospital || '',
          clinicalHistory: cd.clinical_history || cd.clinical || cd.clinicalHistory || dischargeInfo.clinicalHistory || '',
          physicalExam: cd.physical_examination || cd.physical_exam || cd.physical || cd.physicalExam || dischargeInfo.physicalExam || '',
          generalExam: cd.general_examination || cd.general || cd.generalExam || dischargeInfo.generalExam || '',
          systematicExam: cd.systematic_examination || cd.systematic || cd.systematicExam || dischargeInfo.systematicExam || '',
          advice: cd.advice || dischargeInfo.advice || '',
          notes: cd.notes || dischargeInfo.notes || '',
        };
  
        setDischargeInfo(prev => ({
          ...prev,
          diagnosis: mapped.diagnosis,
          procedure: mapped.procedure,
          courseInHospital: mapped.courseInHospital,
          clinicalHistory: mapped.clinicalHistory,
          physicalExam: mapped.physicalExam,
          generalExam: mapped.generalExam,
          systematicExam: mapped.systematicExam,
          advice: mapped.advice,
          notes: mapped.notes,
        }));
  
        // Parse and set medicines from case
        let medicines = [];
        if (cd.medicines) {
          try {
            medicines = typeof cd.medicines === 'string' 
              ? JSON.parse(cd.medicines) 
              : cd.medicines;
            
            // Convert to format with quantities
            if (Array.isArray(medicines)) {
              const medicinesWithFormat = medicines.map(m => {
                const opt = medicineOptions.find(o => o.value == m.med_id);
                return {
                  value: opt ? opt.value : m.med_id,
                  label: opt ? opt.label : m.med_name,
                  morning: String(m.morning || ''),
                  noon: String(m.noon || ''),
                  evening: String(m.evening || ''),
                  night: String(m.night || ''),
                };
              });
              setCaseMedicines(medicinesWithFormat);
              if (medicinesWithFormat.length > 0) {
                toast.info(`${medicinesWithFormat.length} medicine(s) loaded from case`);
              }
            }
          } catch (e) {
            console.error("Error parsing medicines:", e);
            setCaseMedicines([]);
          }
        } else {
          setCaseMedicines([]);
        }
  
        setFormData(prev => {
          const existing = Array.isArray(prev.case) ? [...prev.case] : [];
          const idKey = cd.cat_id ?? cd.case_id ?? cd.id;
          const idx = existing.findIndex(c => String(c.cat_id ?? c.case_id ?? c.id) === String(idKey));
          if (idx === -1 && idKey) existing.push(cd);
          else if (idx !== -1) existing[idx] = { ...existing[idx], ...cd };
          return { ...prev, case: existing };
        });
  
        const name = cd.cat_name || cd.case_title || cd.title || '';
        if (name) setCaseTitle(name);
  
        return cd;
      }
    } catch (err) {
      console.error("loadCaseDetails error:", err);
    }
    return null;
  };

  useEffect(() => {
    if (!selectedCaseId) {
      setCaseMedicines([]);
      return;
    }
    (async () => {
      try {
        await loadCaseDetails(selectedCaseId);
      } catch (err) {
        console.error("useEffect error:", err);
      }
    })();
  }, [selectedCaseId, medicineOptions]);

  const toDateTimeLocalValue = (input) => {
    if (!input && input !== 0) return "";
    const s = String(input).trim();
    const dateTimeWithTime = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})/.test(s);
    if (dateTimeWithTime) {
      const d = new Date(s);
      if (!isNaN(d)) {
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
      return s;
    }

    const dateOnlyRe = /^(\d{4})-(\d{2})-(\d{2})$/;
    const m = s.match(dateOnlyRe);
    if (m) return `${m[1]}-${m[2]}-${m[3]}T00:00`;

    const parsed = new Date(s);
    if (!isNaN(parsed)) {
      const pad = n => String(n).padStart(2, '0');
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    }
    return "";
  };

  const dischargeDateRaw = dischargeInfo?.created_at || dischargeInfo?.dateOfDischarge || patientData?.created_at || '';

  function formatDateTimeAmPm(input) {
    if (!input && input !== 0) return "";
    let d;
    if (input instanceof Date && !isNaN(input)) {
      d = input;
    } else {
      const s = String(input).trim();
      const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
      if (dateOnly) {
        d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
      } else {
        const norm = s.replace(' ', 'T');
        d = new Date(norm);
      }
    }

    if (!d || isNaN(d)) return String(input || "");

    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(d.getDate());
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    const hours24 = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 === 0 ? 12 : (hours24 % 12);
    return `${day}-${month}-${year} ${hours12}:${minutes} ${ampm}`;
  }

  const dischargeDateDisplay = formatDateTimeAmPm(dischargeDateRaw);

  const initData = async () => {
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      const fd = new FormData();
      fd.append('type', "doctor");
      fd.append('search_key', "");
      fd.append('page_no', 0);
      fd.append('user_cre_by', 1 + "");
      try {
        const response = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data.status) {
          const records = response.data.records;
          if (records) setFormData(prev => ({ ...prev, doc: records }));
        }
      } catch (error) {}
    }
  };

  const fetchCaseList = async () => {
    try {
      const fd = new FormData();
      fd.append("cat_type", "case");
      fd.append("page_no", "none");
      fd.append("par_by", "1");
      const response = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data && response.data.status) {
        setFormData(prev => ({ ...prev, case: Array.isArray(response.data.records) ? response.data.records : [] }));
        return response.data.records || [];
      } else {
        setFormData(prev => ({ ...prev, case: [] }));
        return [];
      }
    } catch (err) {
      setFormData(prev => ({ ...prev, case: [] }));
      return [];
    }
  };

  useEffect(() => {
    if (propPatientData) setPatientData(normalizePatient(propPatientData));
    else if (location && location.state && location.state.patientData) setPatientData(normalizePatient(location.state.patientData));
    else {
      if (!modalMode) {
        showSwal({ title: "Error!", text: "No patient data found.", icon: "error" });
        navigate('/add_discharge');
      }
    }
  }, [propPatientData, location, modalMode, navigate]);

  const fetchConfigData = async () => {
    try {
      const res = await axios.post(`${AppConst.BASE_URL + AppConst.getconfig}?config_type=ipd`);
      if (res.data.status) {
        const data = res.data;
        setImage(data.header_logo);
        setWatermarkImage(data.logo || '');
        setFooterImage(data.footer_logo || '');
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (!patientData) return;
    (async () => {
      try {
        await initData();
        await fetchConfigData();
        await fetchCaseList();
        await fetchMedicineList();

       // ✅ Resolve bill id from every possible field
let billIdForLoad = String(
  patientData.ipd_bill_id ||
  patientData.ipd_id ||
  patientData.bill_id ||
  ''
).trim();




// If still empty but we have a UHID, fetch the bill to get its id
// If still empty but we have a UHID, fetch the bill to get its id (ONCE)
if (!billIdForLoad && !billResolvedRef.current &&
  (patientData.ipd_bill_uhid || patientData.ipd_uhid || patientData.uhid)) {
billResolvedRef.current = true;   // ✅ prevent re-runs

const uhid = patientData.ipd_bill_uhid || patientData.ipd_uhid || patientData.uhid || '';
const fdb = new FormData();
fdb.append('ipd_uhid', uhid);

try {
  const billResp = await axios.post(AppConst.BASE_URL + AppConst.getIpdBillByIdUrl, fdb);
  billIdForLoad = String(billResp?.data?.dt?.ipd_bill_id || '').trim();
  const displayNo = billResp?.data?.dt?.ipd_display_no || '';

  if (billIdForLoad) {
    setPatientData(prev => {
      // only update if something actually changed (avoid needless re-render)
      if (prev.ipd_bill_id === billIdForLoad && prev.ipd_display_no === (displayNo || prev.ipd_display_no)) {
        return prev;
      }
      return {
        ...prev,
        ipd_bill_id: billIdForLoad,
        ipd_display_no: displayNo || prev.ipd_display_no,
      };
    });
  }
} catch (e) {
  console.warn('resolve bill id failed', e);
}
}



const fd = new FormData();
fd.append('ipd_bill_id', billIdForLoad);
const res = await axios.post(AppConst.BASE_URL + '/ipd_bill_apis/get_discharge_by_bill_id', fd);

if (res.data && res.data.status && res.data.dt) {
  const dt = res.data.dt;

  setDischargeInfo(prev => ({
    ...prev,
    diagnosis:        dt.diagnosis        || prev.diagnosis,
    // ✅ API uses "procedures" — fall back to either
    procedure:        dt.procedure  || dt.procedures || prev.procedure,
    courseInHospital: dt.course_in_hospital || prev.courseInHospital,
    clinicalHistory:  dt.clinical_history  || prev.clinicalHistory,
    physicalExam:     dt.physical_exam     || prev.physicalExam,
    generalExam:      dt.general_exam      || prev.generalExam,
    systematicExam:   dt.systematic_exam   || prev.systematicExam,
    advice:           dt.advice            || prev.advice,
    notes:            dt.notes             || prev.notes,
    // ✅ pull the saved result back into the dropdown
    result:           dt.result            || prev.result,
    dateOfSurgery:    safeIsoDate(dt.date_of_surgery)    || prev.dateOfSurgery,
    // ✅ datetime-local needs YYYY-MM-DDTHH:mm, not YYYY-MM-DD
    dateOfDischarge:  toDateTimeLocalValue(dt.date_of_discharge) || prev.dateOfDischarge,
    created_at:       dt.created_at        || prev.created_at || "",
  }));

  // ✅ only set case if it's a real id (skip "0" / "" / null)
  const caseIdFromDb = String(dt.case_id || '').trim();
  if (caseIdFromDb && caseIdFromDb !== '0') {
    setSelectedCaseId(caseIdFromDb);
  }

  // ✅ mark as already-saved so the Print button enables on open
  setIsSaved(true);

  // Medicines
  if (dt.medicines) {
    try {
      const medicines = typeof dt.medicines === 'string' ? JSON.parse(dt.medicines) : dt.medicines;
      if (Array.isArray(medicines)) {
        const medicinesWithFormat = medicines.map(m => {
          const opt = medicineOptions.find(o => o.value == m.med_id);
          return {
            value: opt ? opt.value : m.med_id,
            label: opt ? opt.label : m.med_name,
            morning: String(m.morning || ''),
            noon:    String(m.noon    || ''),
            evening: String(m.evening || ''),
            night:   String(m.night   || ''),
          };
        });
        setCaseMedicines(medicinesWithFormat);
      }
    } catch (e) {
      console.error("Error parsing saved medicines:", e);
    }
  }
}

        const cand = patientData.ipd_bill_case || patientData.case_id || patientData.ipd_case || '';
        if (cand) setSelectedCaseId(String(cand));
      } catch (err) {
        console.error("Init error:", err);
      }
    })();
  }, [patientData]);

  function getDoctorNameById(id) {
    const doctor = (formData.doc || []).find(doc => String(doc.user_id) === String(id));
    return doctor ? doctor.fname : " ";
  }

  function getCaseNameById(id) {
    if (!id) return " ";
    const casesArr = formData.case || [];
    const found = casesArr.find(c => String(c.cat_id || c.case_id || c.id) === String(id));
    return found ? (found.cat_name || found.title || " ") : " ";
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDischargeInfo(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!dischargeInfo.dateOfSurgery) {
      setDischargeInfo(d => ({ ...d, dateOfSurgery: patientData?.ipd_bill_ot || patientData?.ipd_bill_date || '' }));
    }
  }, [patientData]);

  const fetchIpdId = async (uhid) => {
    if (patientData) {
      if (patientData.ipd_bill_id) return String(patientData.ipd_bill_id);
      if (patientData.ipd_id) return String(patientData.ipd_id);
    }
    if (uhid) {
      try {
        const fd = new FormData();
        fd.append('ipd_bill_uhid', uhid);
        const res = await axios.post(AppConst.BASE_URL + '/ipd_bill_apis/get_ipd_by_uhid', fd);
        if (res?.data?.status && res.data.dt) {
          const dt = res.data.dt;
          return String(dt.ipd_bill_id ?? dt.ipd_id ?? dt.id ?? '');
        }
      } catch (err) {}
    }
    return '';
  };

  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    try {
      const resp = await fetch(url, { cache: 'no-cache' });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      if (!blob.type.startsWith('image/')) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('imageUrlToBase64 error', err, url);
      return null;
    }
  };

  const generatePDFFromDt = async (recParam = null) => {
    try {
      if (!patientData) {
        showSwal({ title: "Error", text: "Patient data is not available.", icon: "error" });
        return;
      }

      const d = recParam ? ({ ...dischargeInfo, ...recParam }) : dischargeInfo;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const TITLE_SIZE = 13;
      const SECTION_TITLE_SIZE = 10;
      const BODY_SIZE = 9;
      let y = 0;

      let headerBase64 = null;
      try {
        headerBase64 = await imageUrlToBase64(image);
      } catch (e) {
        console.warn("Header load failed", e);
      }

      const reservedHeaderH = headerBase64 ? 80 : 0;
      y += reservedHeaderH + 6;

      doc.setFontSize(BODY_SIZE);
      const patientDetailsBody = [
        [
          { content: `Name`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_patient_name || ''}` },
          { content: `UHID`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_uhid || patientData?.ot_uhid_no || ''}` },
        ],
        [
          { content: `Age`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_patient_age || ''}` },
          { content: `Date of Discharge`, styles: { fontStyle: 'bold' } },
          { content: `${dischargeDateDisplay}` },
        ],
        [
          { content: `Gender`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_patient_gender || ''}` },
          { content: `Contact`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_patient_phone || ''}` },
        ],
        [
          { content: `Address`, styles: { fontStyle: 'bold' } },
          { content: `${patientData?.ipd_bill_patient_address || ''}` },
          { content: `IPD No`, styles: { fontStyle: 'bold' } },
          { content: `${ipdDisplay}` },
        ],
        [
          { content: `Reffered Doctor`, styles: { fontStyle: 'bold' } },
          { content: `${ getDoctorNameById(patientData.ipd_bill_refered_doctor) || patientData.referred_doctor_name || 'N/A' }` },
          { content: `OT Surgeon`, styles: { fontStyle: 'bold' } },
          { content: `${ getDoctorNameById(patientData.ipd_bill_concult_doctor) || patientData.ot_surgeon_name || 'N/A' }` },
        ]
      ];

      autoTable(doc, {
        startY: y,
        body: patientDetailsBody,
        theme: 'plain',
        styles: { fontSize: BODY_SIZE, cellPadding: 2 }
      });

      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : y + 80;

      doc.setFontSize(TITLE_SIZE);
      doc.setFont('helvetica', 'bold');
      doc.text("DISCHARGE SUMMARY", pageW / 2, y, { align: 'center' });
      y += 16;
      doc.setLineWidth(0.5);
      doc.line(10, y, pageW - 10, y);
      y += 8;

      const innerPdfWidthPx = pageW - 40;
      const container = document.createElement('div');
      container.style.width = innerPdfWidthPx + "px";
      container.style.background = "#ffffff";
      container.style.boxSizing = "border-box";
      container.style.fontFamily = "Helvetica, Arial, sans-serif";
      container.style.fontSize = `${BODY_SIZE}pt`;
      container.style.color = "#000";

      const makeSection = (title, html) => {
        const sec = document.createElement('div');
        sec.style.marginBottom = "10px";
        sec.style.pageBreakInside = "avoid";
        sec.style.breakInside = "avoid";
        const h = document.createElement('div');
        h.textContent = title.toUpperCase() + " :";
        h.style.fontWeight = "700";
        h.style.fontSize = `${SECTION_TITLE_SIZE}pt`;
        h.style.textDecoration = "underline";
        h.style.marginBottom = "4px";
        sec.appendChild(h);
        const body = document.createElement('div');
        body.innerHTML = html || "<span style='color:#666'>—</span>";
        body.style.lineHeight = "1.6";
        body.style.fontSize = `${BODY_SIZE}pt`;
        sec.appendChild(body);
        return sec;
      };

      container.appendChild(makeSection("Diagnosis", d.diagnosis));
      container.appendChild(makeSection("Procedure", d.procedure || d.procedures));
      container.appendChild(makeSection("Course in Hospital", d.courseInHospital || d.course_in_hospital));
      container.appendChild(makeSection("Clinical History", d.clinicalHistory || d.clinical_history));

      const ceWrap = document.createElement('div');
      ceWrap.style.pageBreakInside = "avoid";
      ceWrap.style.breakInside = "avoid";
      const ceTitle = document.createElement('div');
      ceTitle.textContent = "CLINICAL EXAMINATION :";
      ceTitle.style.fontWeight = "700";
      ceTitle.style.fontSize = `${SECTION_TITLE_SIZE}pt`;
      ceTitle.style.margin = "10px 0 4px";
      ceWrap.appendChild(ceTitle);
      ceWrap.appendChild(makeSection("Physical Examination", d.physicalExam || d.physical_exam));
      ceWrap.appendChild(makeSection("General Examination", d.generalExam || d.general_exam));
      ceWrap.appendChild(makeSection("Systematic Examination", d.systematicExam || d.systematic_exam));
      container.appendChild(ceWrap);

      // Add Medicines Section with QUANTITY
      if (caseMedicines && caseMedicines.length > 0) {
        const medSection = document.createElement('div');
        medSection.style.marginTop = "12px";
        medSection.style.marginBottom = "10px";
        medSection.style.pageBreakInside = "avoid";
        medSection.style.breakInside = "avoid";
        const medTitle = document.createElement('div');
        medTitle.textContent = "MEDICINES PRESCRIBED :";
        medTitle.style.textDecoration = "underline";
        medTitle.style.fontWeight = "700";
        medTitle.style.fontSize = `${SECTION_TITLE_SIZE}pt`;
        medTitle.style.marginBottom = "6px";
        medSection.appendChild(medTitle);
        
        const medTable = document.createElement('table');
        medTable.style.width = "100%";
        medTable.style.borderCollapse = "collapse";
        medTable.style.fontSize = `${BODY_SIZE}pt`;
        medTable.style.marginTop = "4px";
        
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        headerRow.style.backgroundColor = "#f0f0f0";
        
        ['Sr.', 'Medicine Name', 'Morning', 'Noon', 'Evening', 'Night'].forEach(headerText => {
          const th = document.createElement('th');
          th.textContent = headerText;
          th.style.border = "1px solid #ccc";
          th.style.padding = "6px";
          th.style.textAlign = headerText === 'Medicine Name' ? "left" : "center";
          th.style.fontWeight = "700";
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        medTable.appendChild(thead);
        
        const tbody = document.createElement('tbody');
        caseMedicines.forEach((med, index) => {
          const row = document.createElement('tr');
          
          const td1 = document.createElement('td');
          td1.textContent = (index + 1).toString();
          td1.style.border = "1px solid #ccc";
          td1.style.padding = "6px";
          td1.style.width = "8%";
          td1.style.textAlign = "center";
          row.appendChild(td1);
          
          const td2 = document.createElement('td');
          td2.textContent = med.label || med.med_name || 'N/A';
          td2.style.border = "1px solid #ccc";
          td2.style.padding = "6px";
          td2.style.width = "40%";
          row.appendChild(td2);
          
          const td3 = document.createElement('td');
          td3.textContent = med.morning || '-';
          td3.style.border = "1px solid #ccc";
          td3.style.padding = "6px";
          td3.style.width = "13%";
          td3.style.textAlign = "center";
          row.appendChild(td3);
          
          const td4 = document.createElement('td');
          td4.textContent = med.noon || '-';
          td4.style.border = "1px solid #ccc";
          td4.style.padding = "6px";
          td4.style.width = "13%";
          td4.style.textAlign = "center";
          row.appendChild(td4);
          
          const td5 = document.createElement('td');
          td5.textContent = med.evening || '-';
          td5.style.border = "1px solid #ccc";
          td5.style.padding = "6px";
          td5.style.width = "13%";
          td5.style.textAlign = "center";
          row.appendChild(td5);
          
          const td6 = document.createElement('td');
          td6.textContent = med.night || '-';
          td6.style.border = "1px solid #ccc";
          td6.style.padding = "6px";
          td6.style.width = "13%";
          td6.style.textAlign = "center";
          row.appendChild(td6);
          
          tbody.appendChild(row);
        });
        medTable.appendChild(tbody);
        medSection.appendChild(medTable);
        container.appendChild(medSection);
      }

      container.appendChild(makeSection('Discharge Status', d.result || ''));
      container.appendChild(makeSection('Advice', d.advice || ''));
      container.appendChild(makeSection('Notes', d.notes || ''));

      const footerBlock = document.createElement('div');
      footerBlock.style.marginTop = "10px";
      footerBlock.style.pageBreakInside = "avoid";
      footerBlock.style.breakInside = "avoid";
      footerBlock.innerHTML = `
        <div style="font-weight:700; font-size:${SECTION_TITLE_SIZE}pt; margin-bottom:6px;">
          FILE HANDED OVER DETAILS
        </div>
        <div style="font-size:${BODY_SIZE}pt;">
          <div>NAME: ${patientData.ipd_bill_patient_name || ''}</div>
          <div>Address PATIENT: ${patientData.ipd_bill_patient_address || ''}</div>
          <div>CONTACT NO: ${patientData.ipd_bill_patient_phone || ''}</div>
          <div style="margin-top:30px;">Authorise Signature: ____________________________</div>
          <div style="margin-top:20px;">Doctor Signature: ____________________________</div>
        </div>
      `;
      container.appendChild(footerBlock);

      const htmlTopMargin = reservedHeaderH + 12;
      const yForHtml = Math.max(0, y - htmlTopMargin);

      await new Promise((resolve) => {
        doc.html(container, {
          x: 20,
          y: yForHtml,
          width: innerPdfWidthPx,
          windowWidth: innerPdfWidthPx,
          html2canvas: { scale: 1, useCORS: true, logging: false },
          autoPaging: 'text',
          margin: [htmlTopMargin, 20, 20, 20],
          callback: (docFromHtml) => {
            try {
              const totalPages = docFromHtml.getNumberOfPages();
              const pw = docFromHtml.internal.pageSize.getWidth();
              if (headerBase64) {
                const hdrH = reservedHeaderH;
                for (let p = 1; p <= totalPages; p++) {
                  docFromHtml.setPage(p);
                  docFromHtml.addImage(headerBase64, 'PNG', 0, 0, pw, hdrH);
                }
              }
            } catch (err) {
              console.warn('Header stamping error', err);
            } finally {
              resolve();
            }
          },
        });
      });

      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');

    } catch (err) {
      console.error('generatePDFFromDt error', err);
      showSwal({ title: 'Error', text: 'Could not generate PDF. See console.', icon: 'error' });
    }
  };

  const handleSaveSummary = async (e, type = "save") => {
    e && e.preventDefault && e.preventDefault();
  
    if (!patientData) {
      showSwal({ title: "Error", text: "No patient selected.", icon: "error" });
      return;
    }
  
    const rawIpdBillId = (patientData && (patientData.ipd_bill_id ?? patientData.ipd_id ?? null));
    const ipdBillIdToSend = rawIpdBillId !== null && rawIpdBillId !== undefined && String(rawIpdBillId).trim() !== '' ? String(rawIpdBillId) : '';
    const sessionIpd = (() => { try { return JSON.parse(sessionStorage.getItem("ipd_dt") || sessionStorage.getItem("ipd_bill") || "null"); } catch (err) { return null; } })();
    const sessionUhid = sessionIpd?.ipd_bill_uhid ?? sessionIpd?.ipd_uhid ?? sessionIpd?.uhid ?? '';
    const uhidCandidate = patientData?.ipd_bill_uhid || patientData?.ipd_uhid || patientData?.uhid || sessionUhid || '';
  
    const payloadObj = {
      ipd_bill_id: ipdBillIdToSend,
      ipd_no: patientData?.ipd_no || '',
      ipd_bill_uhid: uhidCandidate,
      case_id: selectedCaseId || '',
      diagnosis: dischargeInfo.diagnosis || '',
      procedure: dischargeInfo.procedure || '',
      course_in_hospital: dischargeInfo.courseInHospital || '',
      clinical_history: dischargeInfo.clinicalHistory || '',
      physical_exam: dischargeInfo.physicalExam || '',
      general_exam: dischargeInfo.generalExam || '',
      systematic_exam: dischargeInfo.systematicExam || '',
      advice: dischargeInfo.advice || '',
      notes: dischargeInfo.notes || '',
      date_of_surgery: dischargeInfo.dateOfSurgery || patientData.ipd_bill_ot || patientData.ipd_bill_date || '',
      date_of_discharge: dischargeInfo.dateOfDischarge || '',
      result: dischargeInfo.result || '',
      user_id: patientData?.ipd_bill_user || JSON.parse(sessionStorage.getItem("ldata") || "{}").user_id || 0,
    };
  
    if (!payloadObj.ipd_bill_uhid && !payloadObj.ipd_bill_id) {
      showSwal({ title: "Missing UHID / IPD ID", text: "Patient UHID or IPD ID is required.", icon: "warning" });
      return;
    }
  
    if (type === "result" && (!payloadObj.result || String(payloadObj.result).trim() === "")) {
      await showSwal({ title: "Missing Result", text: "Please select a Result before finalizing.", icon: "warning" });
      return;
    }
  
    if (type === "result") {
      const confirmed = await showSwal({
        title: "Finalize Discharge?",
        text: "This will save the discharge and close the modal. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, finalize",
        cancelButtonText: "Cancel",
      });
      if (!confirmed.isConfirmed) return;
    }
  
    let ipdIdToSend = patientData?.ipd_id ? String(patientData.ipd_id) : "";
    if (!ipdIdToSend) {
      try {
        ipdIdToSend = await fetchIpdId(payloadObj.ipd_bill_uhid);
        if (ipdIdToSend) console.log("Fetched ipd_id from server:", ipdIdToSend);
      } catch (err) {
        console.warn("fetchIpdId failed", err);
        ipdIdToSend = "";
      }
    }
  
    const fd = new FormData();
    if (payloadObj.ipd_bill_id) fd.append("ipd_bill_id", payloadObj.ipd_bill_id);
    if (ipdIdToSend) fd.append("ipd_id", ipdIdToSend);
    if (uhidCandidate || uhidCandidate === "") {
      fd.append("ipd_bill_uhid", String(uhidCandidate));
      fd.append("ipd_uhid", String(uhidCandidate));
      fd.append("uhid", String(uhidCandidate));
    }
  
    const alwaysAppend = {
      case_id: payloadObj.case_id || "",
      diagnosis: payloadObj.diagnosis || "",
      procedure: payloadObj.procedure || "",
      course_in_hospital: payloadObj.course_in_hospital || "",
      clinical_history: payloadObj.clinical_history || "",
      physical_exam: payloadObj.physical_exam || "",
      general_exam: payloadObj.general_exam || "",
      systematic_exam: payloadObj.systematic_exam || "",
      advice: payloadObj.advice || "",
      notes: payloadObj.notes || "",
      date_of_surgery: payloadObj.date_of_surgery || "",
      date_of_discharge2: payloadObj.created_at || "",
      result: payloadObj.result || "",
      user_id: payloadObj.user_id || 0,
    };
  
    Object.keys(alwaysAppend).forEach((k) => fd.append(k, String(alwaysAppend[k])));
    fd.append("procedures", String(payloadObj.procedure || ""));
  
    // ✅ Send selected medicines as JSON
const medicinesPayload = (caseMedicines || []).map(m => ({
  med_id:  m.value,
  med_name: m.label,
  morning:  m.morning || '',
  noon:     m.noon    || '',
  evening:  m.evening || '',
  night:    m.night   || '',
}));
fd.append('medicines', JSON.stringify(medicinesPayload));
    try {
      const saveRes = await axios.post(AppConst.BASE_URL + "/ipd_bill_apis/save_discharge_summary", fd);
      console.log("save_discharge_summary response:", saveRes.data);
  
      if (saveRes.data && saveRes.data.status) {
        const savedDt = saveRes.data.dt || null;
        setIsSaved(true);
        toast.success("Discharge summary saved");
  
        if (savedDt) {
          setDischargeInfo((prev) => ({
            ...prev,
            diagnosis: savedDt.diagnosis ?? prev.diagnosis,
            procedure: savedDt.procedure ?? savedDt.procedures ?? prev.procedure,
            courseInHospital: savedDt.course_in_hospital ?? prev.courseInHospital,
            clinicalHistory: savedDt.clinical_history ?? prev.clinicalHistory,
            physicalExam: savedDt.physical_exam ?? prev.physicalExam,
            generalExam: savedDt.general_exam ?? prev.generalExam,
            systematicExam: savedDt.systematic_exam ?? prev.systematicExam,
            advice: savedDt.advice ?? prev.advice,
            notes: savedDt.notes ?? prev.notes,
            dateOfSurgery: savedDt.date_of_surgery ? new Date(savedDt.date_of_surgery).toISOString().split("T")[0] : prev.dateOfSurgery,
            dateOfDischarge: savedDt.date_of_discharge ? new Date(savedDt.date_of_discharge).toISOString().split("T")[0] : prev.dateOfDischarge,
            result: savedDt.result ?? prev.result,
            created_at: savedDt.created_at ?? prev.created_at ?? "",
          }));
        }
  
        if (type === "result") {
          if (typeof onSaved === "function") {
            try { onSaved({ success: true, data: saveRes.data, autoClose: true }); } catch (e) { console.warn(e); }
          }
          if (typeof onClose === "function") {
            try { onClose(); } catch (e) { console.warn(e); }
          }
  
          const r = await showSwal({
            title: "Discharge saved",
            text: "Do you want to print the discharge summary now?",
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Print",
            cancelButtonText: "Cancel",
            allowOutsideClick: false,
          });
  
          if (r.isConfirmed) {
            await generatePDFFromDt(savedDt || payloadObj);
          }
        } else {
          if (typeof onSaved === "function") {
            try { onSaved({ success: true, data: saveRes.data, autoClose: false }); } catch (e) { console.warn(e); }
          }
        }
      } else {
        showSwal({ title: "Could not save", text: saveRes.data?.message || "Server returned failure.", icon: "error" });
      }
    } catch (err) {
      console.error("save_discharge_summary error:", err);
      const serverData = err.response?.data || err.response?.statusText || err.message;
      showSwal({ title: "Could not save", text: serverData?.message || JSON.stringify(serverData) || "Server error.", icon: "error" });
    }
  };

  const handlePrintClick = async () => {
    if (!isSaved) {
      await showSwal({ title: "Kindly Save the Discharge Sheet", text: "", icon: "warning" });
      return;
    }
    await generatePDFFromDt();
  };

  if (!patientData) return <div>Loading...</div>;

  const admissionDate = patientData.ipd_bill_date;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <button className="btn btn-danger" onClick={() => {
                try {
                  const payload = { ...patientData, ipd_case: patientData.ipd_case || patientData.case_id || patientData.ipd_bill_case || "", ipd_bill_uhid: patientData.ipd_bill_uhid || patientData.ipd_uhid || '', ipd_bill_id: patientData.ipd_bill_id || patientData.ipd_id || '', services: patientData.services || patientData.ipd_services || [] };
                  sessionStorage.setItem("ot_dt", JSON.stringify(payload));
                  sessionStorage.setItem("ipd_dt", JSON.stringify(payload));
                  sessionStorage.setItem("ipd_bill", JSON.stringify(payload));
                  if (modalMode && typeof onClose === 'function') onClose();
                  else navigate('/ipd_billing');
                } catch (err) {
                  console.error("Error storing session data before navigate:", err);
                  if (modalMode && typeof onClose === 'function') onClose();
                  else navigate('/ipd_billing');
                }
              }}>
              ← Back
            </button>
            <h5 className="card-title mb-0">Discharge Summary</h5>
            <div></div>
          </div>

          <div className="card-body">
            <h6 className="fw-bold mb-3">Patient Details</h6>
            <div className="row">
              <div className="col-lg-6">
                <table className="table table-bordered">
                  <tbody>
                    <tr><td>Name:</td><td>{patientData.ipd_bill_patient_name}</td></tr>
                    <tr><td>UHID:</td><td>{patientData.ipd_bill_uhid || patientData.ot_uhid_no || 'N/A'}</td></tr>
                    <tr><td>Age:</td><td>{patientData.ipd_bill_patient_age}</td></tr>
                    <tr><td>Gender:</td><td>{patientData.ipd_bill_patient_gender}</td></tr>
                    <tr><td>Address:</td><td>{patientData.ipd_bill_patient_address}</td></tr>
                    <tr><td>Ot Doctor:</td><td>{getDoctorNameById(patientData.ipd_bill_concult_doctor)}</td></tr>
                    <tr><td>Contact:</td><td>{patientData.ipd_bill_patient_phone}</td></tr>
                    <tr><td>Ref Doctor:</td><td>{getDoctorNameById(patientData.ipd_bill_refered_doctor) || 'N/A'}</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="col-lg-6">
                <table className="table table-bordered">
                  <tbody>
                    <tr><td>Admission Date:</td><td>{admissionDate}</td></tr>
                    <tr>
                      <td>Date of Surgery:</td>
                      <td><input type="date" className="form-control" name="dateOfSurgery" value={dischargeInfo.dateOfSurgery || patientData.ipd_bill_ot || ''} onChange={handleChange} /></td>
                    </tr>
                    <tr>
                      <td>Date of Discharge:</td>
                      <td><input type="datetime-local" className="form-control" name="dateOfDischarge" value={dischargeInfo.dateOfDischarge || toDateTimeLocalValue(patientData.date_of_discharge || patientData.created_at || '') || getNowDateTimeLocal()} onChange={handleChange} required /></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Case</label>
                <h1>{getCaseNameById(selectedCaseId || patientData.ipd_bill_case) || caseTitle || 'N/A'}</h1>
              </div>

              {/* Medicine Management Section */}
              <div className="col-12 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-bold mb-0">
                    <i className="ri-medicine-bottle-line me-2"></i>Prescribed Medicines
                  </label>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowMedicineModal(true)}>
                    <i className="ri-add-line me-1"></i>{caseMedicines.length > 0 ? 'Edit Medicines' : 'Add Medicines'}
                  </button>
                </div>
                {caseMedicines && caseMedicines.length > 0 ? (
                  <div className="card border-primary">
                    <div className="card-body p-2">
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '5%' }}>Sr.</th>
                              <th style={{ width: '40%' }}>Medicine Name</th>
                              <th className="text-center" style={{ width: '12%' }}>Morning</th>
                              <th className="text-center" style={{ width: '12%' }}>Noon</th>
                              <th className="text-center" style={{ width: '12%' }}>Evening</th>
                              <th className="text-center" style={{ width: '12%' }}>Night</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caseMedicines.map((med, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                <td>{med.label || med.med_name || 'N/A'}</td>
                                <td className="text-center">{med.morning || '-'}</td>
                                <td className="text-center">{med.noon || '-'}</td>
                                <td className="text-center">{med.evening || '-'}</td>
                                <td className="text-center">{med.night || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-warning mb-0">
                    <i className="ri-information-line me-2"></i>No medicines prescribed. Click "Add Medicines" to add.
                  </div>
                )}
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Result</label>
                <select className="form-select" name="result" required value={dischargeInfo.result} onChange={e => setDischargeInfo(prev => ({ ...prev, result: e.target.value }))}>
                  <option value="">-- Select Result --</option>
                  <option value="Discharged">Discharged</option>
                  <option value="Referred">Referred</option>
                  <option value="LAMA">LAMA</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>

              <div className="row">
                <EditorPanel id="ed-diagnosis" key="diagnosis" label="Diagnosis" value={dischargeInfo.diagnosis || ''} open={!!editorOpen.diagnosis} onToggle={() => toggleEditor('diagnosis')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, diagnosis: data }))} />
                <EditorPanel id="ed-procedure" key="procedure" label="Procedure" value={dischargeInfo.procedure || ''} open={!!editorOpen.procedure} onToggle={() => toggleEditor('procedure')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, procedure: data }))} />
                <EditorPanel id="ed-course" key="course" label="Course in Hospital" value={dischargeInfo.courseInHospital || ''} open={!!editorOpen.courseInHospital} onToggle={() => toggleEditor('courseInHospital')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, courseInHospital: data }))} />
                <EditorPanel id="ed-clinical" key="clinical" label="Clinical History" value={dischargeInfo.clinicalHistory || ''} open={!!editorOpen.clinicalHistory} onToggle={() => toggleEditor('clinicalHistory')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, clinicalHistory: data }))} />
                <EditorPanel id="ed-physical" key="physical" label="Physical Examination" value={dischargeInfo.physicalExam || ''} open={!!editorOpen.physicalExam} onToggle={() => toggleEditor('physicalExam')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, physicalExam: data }))} />
                <EditorPanel id="ed-general" key="general" label="General Examination" value={dischargeInfo.generalExam || ''} open={!!editorOpen.generalExam} onToggle={() => toggleEditor('generalExam')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, generalExam: data }))} />
                <EditorPanel id="ed-systematic" key="systematic" label="Systematic Examination" value={dischargeInfo.systematicExam || ''} open={!!editorOpen.systematicExam} onToggle={() => toggleEditor('systematicExam')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, systematicExam: data }))} />
                <EditorPanel id="ed-advice" key="advice" label="Advice" value={dischargeInfo.advice || ''} open={!!editorOpen.advice} onToggle={() => toggleEditor('advice')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, advice: data }))} />
                <EditorPanel id="ed-notes" key="notes" label="Notes" value={dischargeInfo.notes || ''} open={!!editorOpen.notes} onToggle={() => toggleEditor('notes')} onChange={(data) => setDischargeInfo(prev => ({ ...prev, notes: data }))} />
              </div>

              <div className="text-end mt-4 col-12">
                <button type="button" className="btn btn-light me-2" onClick={() => { if (modalMode && typeof onClose === 'function') onClose(); else navigate('/add_discharge'); }}>Cancel</button>
                <button type="button" className="btn btn-success" onClick={(e) => handleSaveSummary(e, "result")}>Save & Finalize Result</button>
                <button type="button" className="btn btn-danger ms-2" disabled={!isSaved} onClick={handlePrintClick}>Print</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Medicine Modal */}
      <MedicineModal show={showMedicineModal} onClose={() => setShowMedicineModal(false)} medicineOptions={medicineOptions} selectedMedicines={caseMedicines} onSave={handleMedicineSave} loading={medicineLoading} />
    </div>
  );
}

export default DischargeSummary;